import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { View, type ViewStyle } from 'react-native';
import { useChatContext, type IMessage } from 'react-native-gifted-chat';
import { twJoin } from 'tailwind-merge';

import { CustomMessageProps, StyledCustomMessage } from '@/components/chat';

type ChatMessageContainerProps = CustomMessageProps & {
  fillRow: boolean;
};

export function ChatMessageContainer({ fillRow, ...props }: ChatMessageContainerProps) {
  const { nextMessage, previousMessage, position } = props;

  const isFirstMessage = !previousMessage?.user?._id;

  const isLastMessage = !nextMessage?.user?._id;

  const baseClassName = twJoin(isFirstMessage && 'pt-0', isLastMessage && 'pb-3');

  /** Flex + web: long bubbles need min-w-0 and max-w-full so text wraps instead of clipping horizontally */
  const widthConstraint = 'max-w-full min-w-0';

  return (
    <View
      testID={`chat-message-${position}`}
      className="w-full max-w-full"
      style={{ marginBottom: 8 }}>
      <StyledCustomMessage
        {...props}
        rightContainerClassName={twJoin(
          baseClassName,
          widthConstraint,
          'self-end pr-0',
          fillRow ? 'mr-0' : 'mr-0'
        )}
        leftContainerClassName={twJoin(
          baseClassName,
          widthConstraint,
          fillRow ? 'ml-0 mr-0' : 'self-start ml-0'
        )}
      />
    </View>
  );
}

export function ChatCustomBubbleContainer({
  children,
  position = 'left',
}: {
  children: React.ReactNode;
  position?: 'left' | 'right';
}) {
  const containerPaddingClass = getChatCustomBubblePaddingClass(position);

  return (
    <View
      className={twJoin('w-full max-w-full', containerPaddingClass)}
      style={{ marginBottom: 8 }}>
      <View
        className={twJoin('min-w-0 max-w-full', position === 'right' ? 'self-end' : 'self-start')}>
        {children}
      </View>
    </View>
  );
}

function getChatCustomBubblePaddingClass(position: 'left' | 'right') {
  if (position === 'right') {
    return isWeb ? 'pr-5' : 'pr-2';
  }

  return isWeb ? 'pl-5' : 'pl-2';
}

/**
 * Web only: right-clicking a message row opens the same menu a long press does.
 * Native rows render as they are, since there is no right click to catch.
 */
export function MessageContextMenuTarget({
  message,
  onContextMenu,
  children,
}: {
  message: IMessage;
  onContextMenu?: (chatContext: unknown, message: IMessage) => boolean;
  children: React.ReactNode;
}) {
  if (!isWeb || !onContextMenu) {
    return <>{children}</>;
  }

  return (
    <WebContextMenuTarget message={message} onContextMenu={onContextMenu}>
      {children}
    </WebContextMenuTarget>
  );
}

function WebContextMenuTarget({
  message,
  onContextMenu,
  children,
}: {
  message: IMessage;
  onContextMenu: (chatContext: unknown, message: IMessage) => boolean;
  children: React.ReactNode;
}) {
  const chatContext = useChatContext();

  const viewProps = {
    onContextMenu: (event: ReactMouseEvent) => {
      if (shouldKeepBrowserMenu(event)) {
        return;
      }

      if (onContextMenu(chatContext, message)) {
        event.preventDefault();
      }
    },
  };

  return (
    <View style={{ display: 'contents' } as ViewStyle} {...viewProps}>
      {children}
    </View>
  );
}

type ReactMouseEvent = {
  preventDefault: () => void;
  target: EventTarget | null;
  nativeEvent?: { pointerType?: string };
};

/**
 * The browser's own menu stays for a touch long press, which GiftedChat's long
 * press already handles, and for a link, an image or selected text, which have
 * menu items of their own.
 */
export function shouldKeepBrowserMenu(event: ReactMouseEvent): boolean {
  if (event.nativeEvent?.pointerType === 'touch') {
    return true;
  }

  const target = event.target as Element | null;

  if (target?.closest?.('a, img')) {
    return true;
  }

  return Boolean(window.getSelection?.()?.toString());
}
